import { Component, lazy, Suspense, useEffect, useState, type ReactNode } from "react";
import { AccountProvider, useAccount } from "./AccountContext.js";
import { firebaseConfigured, firebaseRequired } from "./firebase.js";
import PublicDirectory from "./PublicDirectory.js";
import { PRIVATE_SITE_PREFIXES } from "@puntopy/contracts";
import { applyPageSeo, privatePageSeo } from "./seo.js";
import NotFoundPage from "./NotFoundPage.js";
import PwaInstallNotice from "./PwaInstallNotice.js";

const AccountPage = lazy(() => import("./AccountPage.js"));
const BusinessProfilePage = lazy(() => import("./BusinessProfilePage.js"));
const MerchantInvitePage = lazy(() => import("./MerchantInvitePage.js"));
const DownloadAppPage = lazy(() => import("./DownloadAppPage.js"));
const PrivacyPage = lazy(() => import("./LegalPages.js").then((module) => ({ default: module.PrivacyPage })));
const TermsPage = lazy(() => import("./LegalPages.js").then((module) => ({ default: module.TermsPage })));
const AccountDeletionPage = lazy(() => import("./LegalPages.js").then((module) => ({ default: module.AccountDeletionPage })));

class AppErrorBoundary extends Component<{ children: ReactNode }, { failed: boolean }> {
  state = { failed: false };
  static getDerivedStateFromError() { return { failed: true }; }
  render() {
    if (this.state.failed) return <main className="mp-crash"><img src="/brand/puntopy-logo-horizontal.svg" alt="PuntoPY" /><h1>No pudimos abrir esta pantalla</h1><p>Tu información sigue guardada. Recargá la página para volver a conectar.</p><button className="mp-button primary" onClick={() => window.location.reload()}>Volver a intentar</button><a href="/">Ir al inicio</a></main>;
    return this.props.children;
  }
}

function LegacyOperations() {
  const session = useAccount();
  return <AccountPage section={new URLSearchParams(window.location.search).has("invite") ? "merchant" : session.state?.canAccessOperations || (!firebaseConfigured && !firebaseRequired) ? "admin" : session.state?.account?.accountType === "BUSINESS" ? "merchant" : "account"} />;
}

function Routes() {
  const pathname = window.location.pathname.replace(/\/+$/, "") || "/";
  useEffect(() => {
    if (PRIVATE_SITE_PREFIXES.some((prefix) => pathname === prefix || pathname.startsWith(prefix + "/"))) {
      applyPageSeo({ ...privatePageSeo, title: document.title });
    }
  }, [pathname]);
  const [welcome, setWelcome] = useState(() => {
    if (pathname !== "/") return false;
    try { return sessionStorage.getItem("puntopy:welcome-shown") !== "1"; } catch { return false; }
  });
  useEffect(() => {
    if (!welcome) return;
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const timeout = window.setTimeout(() => {
      try { sessionStorage.setItem("puntopy:welcome-shown", "1"); } catch { /* Opcional. */ }
      setWelcome(false);
    }, reduced ? 0 : 850);
    return () => window.clearTimeout(timeout);
  }, [welcome]);
  if (pathname === "/") return <><PublicDirectory /><PwaInstallNotice />{welcome && <div className="welcome-screen welcome-overlay" aria-hidden="true"><div className="welcome-mark"><img src="/brand/puntopy-logo-dark.svg" alt="PuntoPY" /><span className="welcome-line" /><p>Encontrá tu punto de conexión.</p></div></div>}</>;
  if (pathname === "/descargar") return <DownloadAppPage />;
  if (pathname === "/terminos") return <TermsPage />;
  if (pathname === "/privacidad") return <PrivacyPage />;
  if (pathname === "/eliminar-cuenta") return <AccountDeletionPage />;
  if (pathname === "/incorporar" || pathname.startsWith("/incorporar/")) return <MerchantInvitePage />;
  if (pathname.startsWith("/comercio/")) return <BusinessProfilePage />;
  if (pathname === "/administracion" || pathname.startsWith("/administracion/")) return <AccountPage section="admin" />;
  if (pathname === "/mi-comercio") return <AccountPage section="merchant" />;
  if (pathname === "/cuenta" || pathname === "/ingresar" || pathname === "/registro") return <AccountPage section="account" />;
  if (pathname === "/operaciones" || pathname.startsWith("/operaciones/")) {
    return <LegacyOperations />;
  }
  return <NotFoundPage />;
}

export default function App() {
  return <AppErrorBoundary><AccountProvider><Suspense fallback={<main className="mp-crash"><img src="/brand/puntopy-logo-horizontal.svg" alt="PuntoPY" /><p role="status">Abriendo PuntoPY…</p></main>}><Routes /></Suspense></AccountProvider></AppErrorBoundary>;
}
